import { Link } from "react-router-dom";
import { ArrowUpRight, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/motion";
import { values, processSteps } from "@/data/site";
import { categories } from "@/data/services";
import { usePageMeta } from "@/lib/api";

export default function AboutPage() {
  usePageMeta(
    "About SarvaRitam | ESG & Sustainability Advisory",
    "SarvaRitam brings together ESG strategy, sustainability, climate, environmental and social expertise to improve the quality of decisions."
  );
  return (
    <>
      <PageHero
        eyebrow="About SarvaRitam"
        title={[<>We believe sustainability should improve <span className="serif-it text-gold">the quality of decisions.</span></>]}
        intro="SarvaRitam brings together ESG strategy, sustainability, climate, environmental and social expertise to help organisations navigate increasingly complex expectations from regulators, investors, employees, customers and communities."
      />

      <section className="bg-offwhite text-charcoal sec" data-testid="about-philosophy">
        <div className="wrap grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow text-forest mb-6">Our philosophy</p>
              <h2 className="h-display font-semibold text-3xl lg:text-[40px] leading-[1.1]">
                Measure. Understand. <span className="serif-it text-forest">Transform.</span>
              </h2>
              <p className="text-charcoal/65 text-[15.5px] leading-relaxed mt-7">
                Sustainability programmes fail in one of two ways: they stay abstract, or they drown in paperwork.
                Our philosophy sits between — grounded in evidence, aimed at decisions, and honest about what the
                data does and does not show.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.05}>
                <div className="h-full border rule-light bg-white/60 p-7">
                  <span className="font-mono text-[11px] text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display font-semibold text-lg mt-3">{v.title}</h3>
                  <p className="text-charcoal/60 text-[13.5px] mt-2.5 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-offwhite sec relative grain" data-testid="about-approach">
        <div className="wrap relative grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="eyebrow text-gold mb-6">Our approach</p>
              <h2 className="h-display font-semibold text-3xl lg:text-4xl leading-tight">A method, not a template.</h2>
              <p className="text-white/80 text-[15px] leading-relaxed mt-6">
                Every engagement moves through the same disciplined sequence — scaled to the organisation.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10">
            {processSteps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.05}>
                <div className="flex items-baseline gap-5 py-5 border-b border-white/10">
                  <span className="font-display font-bold text-2xl text-gold/30">{s.num}</span>
                  <div>
                    <h3 className="font-display font-semibold text-[17px]">{s.title}</h3>
                    <p className="text-white/75 text-[13px] mt-1.5 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-offwhite text-charcoal sec" data-testid="about-expertise">
        <div className="wrap">
          <Reveal>
            <p className="eyebrow text-forest mb-6">Our expertise</p>
            <h2 className="h-display font-semibold text-3xl lg:text-4xl leading-tight max-w-2xl mb-12">
              Integrated across the whole ESG stack.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.05}>
                <Link to={`/services#${c.id}`} className="lift group block h-full border rule-light bg-white/60 hover:border-forest p-7" data-testid={`about-expertise-${c.id}`}>
                  <p className="font-mono text-[10.5px] uppercase tracking-widest2 text-sage">{c.name}</p>
                  <p className="text-[13.5px] text-charcoal/65 mt-4 leading-relaxed">{c.tagline}</p>
                  <span className="inline-flex items-center gap-1.5 mt-5 font-bold text-[12px] text-forest">Explore <ArrowUpRight size={12} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand/40 text-charcoal sec" data-testid="about-leadership">
        <div className="wrap grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full bg-offwhite border rule-light p-10 sm:p-12">
              <p className="eyebrow text-forest mb-5">Leadership</p>
              <h2 className="h-display font-semibold text-2xl sm:text-3xl leading-tight">Profiles to be published.</h2>
              <p className="text-charcoal/60 text-[14.5px] mt-5 leading-relaxed max-w-md">
                Leadership profiles and advisory bios will be published here as SarvaRitam's team page is finalised —
                we don't publish placeholder names or borrowed credentials.
              </p>
              <div className="mt-8 space-y-3">
                {["[Leadership name] — role placeholder", "[Advisory board member] — role placeholder"].map((t) => (
                  <div key={t} className="flex items-center gap-4 border rule-light p-4 bg-white/50">
                    <span className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center text-forest/50"><Users size={16} /></span>
                    <span className="font-mono text-[11px] text-charcoal/45">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full bg-forest text-offwhite p-10 sm:p-12 relative grain overflow-hidden">
              <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(60% 70% at 20% 15%, rgba(198,161,91,0.5), transparent 65%)" }} />
              <div className="relative">
                <p className="eyebrow text-gold mb-5">Advisory network</p>
                <h2 className="h-display font-semibold text-2xl sm:text-3xl leading-tight">Specialists, assembled per engagement.</h2>
                <p className="text-white/85 text-[14.5px] mt-5 leading-relaxed">
                  SarvaRitam works with a network of domain advisors across climate, nature, social performance and
                  sustainable finance — sized to the engagement, never inflated.
                </p>
                <Link to="/contact" className="btn-gold mt-9" data-testid="about-network-cta">
                  Work with us <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
